// @ts-nocheck
import React, { useState, useEffect, useContext, useRef } from 'react';
import Router from 'next/router';
import { useQuery } from '@tanstack/react-query';
import SettingsMenu from '@/components/settings/menu';
import { colors, layout } from '@/theme';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import Button from '@/components/button';
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import SCCheckbox from '@/components/sc-controls/form-controls/sc-checkbox';
import SCTextArea from '@/components/sc-controls/form-controls/sc-textarea';
import SCCombobox from '@/components/sc-controls/form-controls/sc-combobox';
import ToastContext from '@/utils/toast-context';
import ConfirmAction from '@/components/modals/confirm-action';
import SCUploadDropzone from '@/components/sc-controls/form-controls/sc-upload-dropzone';
import Storage from '@/utils/storage';
import CompanyService from '@/services/company-service';
import PS from '@/services/permission/permission-service';
import constants from '@/utils/constants';
import SCNumericInput from "@/components/sc-controls/form-controls/sc-numeric-input";
import { Tooltip, Flex, Text } from '@mantine/core';
import {IconInfoCircle, IconPlus} from '@tabler/icons-react';
import NewBadge from "@/components/NewBadge";
import { createFileRoute } from '@tanstack/react-router'



function ManageCompany() {

    let currPath = "";
    if (Router.router) {
        currPath = Router.router.asPath;
    }

    const [company, setCompany] = useState<any>(null);
    const [saving, setSaving] = useState(false);
    const [formIsDirty, setFormIsDirty] = useState(false);
    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
    const [uploading, setUploading] = useState(false);
    const [inputErrors, setInputErrors] = useState<any>({});
    const toast = useContext(ToastContext);
    const [resetValues, setResetValues] = useState(false);

    useEffect(() => {
        getAccessStatus();
    }, []);

    const getAccessStatus = () => {
        let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
        if (subscriptionInfo) {
            setAccessStatus(subscriptionInfo.AccessStatus);
        }
    };

    const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);

    useEffect(() => {
        if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
            Helper.nextRouter(Router.replace, "/");
        }
    }, [accessStatus]);

    Helper.preventRouteChange(formIsDirty, setFormIsDirty, setConfirmOptions, () => {
        return saveCompany();
    });

    const updateCompany = (field: any, value: any) => {
        let temp = { ...company };
        temp[field] = value;
        setCompany(temp);
        setFormIsDirty(true);
    };

    const handleInputChange = (e: any) => {
        updateCompany([e.name], e.value);
        setFormIsDirty(true);
    };

    const getFormattedWhatsApp = (value: any) => {
        if (value && value.toString().startsWith('0')) {
            return '27' + value.toString().substring(1);
        }
        return value;
    };

    const handleWhatsAppChange = (e: any) => {
        updateCompany('WhatsAppNumber', e.value);
    };

    const handleWhatsAppBlur = () => {
        const formatted = getFormattedWhatsApp(company.WhatsAppNumber);
        if (formatted !== company.WhatsAppNumber) {
            updateCompany('WhatsAppNumber', formatted);
        }
    };

    const getAddresses = (company: any) => {
        let address = extractAddress(company.AddressLine1, company.AddressLine2, company.AddressLine3, company.AddressLine4);
        let postalAddress = extractAddress(company.PostalLine1, company.PostalLine2, company.PostalLine3, company.PostalLine4);
        setAddress(address);
        setPostalAddress(postalAddress);
        setSameAddress(!Helper.isNullOrWhitespace(address.trim()) && address.trim() == postalAddress.trim());
    };

    const extractAddress = (address1: any, address2: any, address3: any, address4: any) => {
        let address = '';
        let hasLine1 = !Helper.isNullOrWhitespace(address1);
        let hasLine2 = !Helper.isNullOrWhitespace(address2);
        let hasLine3 = !Helper.isNullOrWhitespace(address3);
        let hasLine4 = !Helper.isNullOrWhitespace(address4);

        if (hasLine1) {
            address += address1;
        }
        if (hasLine2) {
            address += `${hasLine1 ? "\n" : ""}${address2}`;
        }
        if (hasLine3) {
            address += `${hasLine1 || hasLine2 ? "\n" : ""}${address3}`;
        }
        if (hasLine4) {
            address += `${hasLine1 || hasLine2 || hasLine3 ? "\n" : ""}${address4}`;
        }
        return address;
    };

    const getAddressLines = (address: any) => {
        let line1 = null;
        let line2 = null;
        let line3 = null;
        let line4 = null;

        let addressLines = address.split("\n").filter((x: any) => !Helper.isNullOrWhitespace(x));
        if (addressLines.length > 0) {
            if (addressLines.length >= 1) line1 = addressLines[0].trim();
            if (addressLines.length >= 2) line2 = addressLines[1].trim();
            if (addressLines.length >= 3) line3 = addressLines[2].trim();
            if (addressLines.length == 4) line4 = addressLines[3].trim();
        }

        return { line1, line2, line3, line4 };
    };

    const [address, setAddress] = useState('');

    const handleAddressChange = (e: any) => {
        setAddress(e.value);
        setFormIsDirty(true);
    };

    const [sameAddress, setSameAddress] = useState(false);

    const handleSameAddressChange = () => {
        let newValue = !sameAddress;

        if (newValue) {
            setPostalAddress(address);
        }

        setSameAddress(newValue);
    };

    const [postalAddress, setPostalAddress] = useState('');

    const handlePostalAddressChange = (e: any) => {
        setPostalAddress(e.value);
        setFormIsDirty(true);
    };

    const [currencies, setCurrencies] = useState<any>(null);

    const [selectedCurrency, setSelectedCurrency] = useState<any>(null);
    const [selectedTimezone, setSelectedTimezone] = useState<any>(null);

    const { data: companyData } = useQuery(
        ['company'],
        () => CompanyService.getCompany()
    );

    const { data: currenciesData } = useQuery(
        ['currencies'],
        () => CompanyService.getCurrencies()
    );

    useEffect(() => {
        if (companyData && currenciesData) {
            setCompany(companyData);
            setCurrencies(currenciesData.Results);
            setSelectedCurrency(companyData.Currency);
            setSelectedTimezone(companyData.Timezones?.find((x: any) => x.Id === companyData.Timezone) ?? null);
            getAddresses(companyData);
        }
    }, [companyData, currenciesData]);

    const handleCurrencyChange = (value: any) => {
        setSelectedCurrency(value);
        setFormIsDirty(true);
    };


    const handleTimezoneChange = (value: any) => {
        const newId = value ? value.Id : null;
        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            display: true,
            heading: "Timezone change",
            confirmButtonText: "Confirm",
            text: "Please note, changing timezone will require all connected mobile devices to resynchronise their data. Confirm?",
            onConfirm: () => {
                setSelectedTimezone(value);
                updateCompany("Timezone", newId);
                setFormIsDirty(true);
            },
            onCancel: () => {
                setResetValues((rv: any) => !rv);
            }
        });
    };

    const validate = () => {
        let validationItems = [
            { key: 'Name', value: company.Name, required: true, type: Enums.ControlType.Text },
            { key: 'EmailAddress', value: company.EmailAddress, type: Enums.ControlType.Email },
            { key: 'EmailFromAddress', value: company.EmailFromAddress, type: Enums.ControlType.Email },
            { key: 'TelephoneNumber', value: company.TelephoneNumber, type: Enums.ControlType.ContactNumber },
            { key: 'WhatsAppNumber', value: getFormattedWhatsApp(company.WhatsAppNumber), type: Enums.ControlType.ContactNumber },
            { key: 'TaxPercentage', value: company.TaxPercentage, gte: 0, type: Enums.ControlType.Number },
            { key: 'Timezone', value: company.Timezone, required: true, type: Enums.ControlType.Text },
        ];

        let { isValid, errors } = Helper.validateInputs(validationItems);

        let addressLines = address.split("\n").filter((x: any) => !Helper.isNullOrWhitespace(x));
        if (addressLines.length > 4) {
            errors["Address"] = "You can only have up to 4 address lines";
            isValid = false;
        } else if (addressLines.some((line: any) => line.length > 100)) {
            errors["Address"] = "Each address line must be 100 characters or less";
            isValid = false;
        } else {
            errors["Address"] = null;
        }

        let postalAddressLines = postalAddress.split("\n").filter((x: any) => !Helper.isNullOrWhitespace(x));
        if (postalAddressLines.length > 4) {
            errors["PostalAddress"] = "You can only have up to 4 address lines";
            isValid = false;
        } else if (postalAddressLines.some((line: any) => line.length > 100)) {
            errors["PostalAddress"] = "Each address line must be 100 characters or less";
            isValid = false;
        } else {
            errors["PostalAddress"] = null;
        }

        setInputErrors(errors);
        return isValid;
    };

    const onUploadLogoChange = (e: any) => {

        let isValid = validate();
        if (isValid) {
            let reader = new FileReader();
            let file = e.target.files[0];
            reader.onloadend = async function () {
                let b64 = (reader.result as any).replace(/^data:.+;base64,/, '');

                let params = { ...company, WhatsAppNumber: getFormattedWhatsApp(company.WhatsAppNumber), FileBase64: b64, CurrencyID: selectedCurrency ? selectedCurrency.ID : null };
                setUploading(true);
                const response = await CompanyService.saveCompany(params, toast);
                setUploading(false);

                if (response.Name) {
                    toast.setToast!!!({
                        message: 'Logo uploaded successfully',
                        show: true,
                        type: Enums.ToastType.success
                    });
                    setCompany(response);
                    setFormIsDirty(false);
                }
            };
            reader.readAsDataURL(file);
        } else {
            toast.setToast!!!({
                message: 'There are errors on the page',
                show: true,
                type: Enums.ToastType.error
            });
        }

    };

    const saveCompany = async () => {

        setSaving(true);

        let isValid = validate();
        if (isValid) {

            const addressLines = getAddressLines(address);
            const postalAddressLines = sameAddress ? addressLines : getAddressLines(postalAddress);

            let params = {
                ...company,
                WhatsAppNumber: getFormattedWhatsApp(company.WhatsAppNumber),
                AddressLine1: addressLines.line1,
                AddressLine2: addressLines.line2,
                AddressLine3: addressLines.line3,
                AddressLine4: addressLines.line4,
                PostalLine1: postalAddressLines.line1,
                PostalLine2: postalAddressLines.line2,
                PostalLine3: postalAddressLines.line3,
                PostalLine4: postalAddressLines.line4,
            };

            params = { ...params, CurrencyID: selectedCurrency ? selectedCurrency.ID : null };

            const response = await CompanyService.saveCompany(params, toast);

            if (response.Name) {
                toast.setToast!!!({
                    message: 'Company saved successfully',
                    show: true,
                    type: Enums.ToastType.success
                });

                Helper.mixpanelTrack(constants.mixPanelEvents.editCompany, {});

                setCompany(response);
                getAddresses(response);
                setFormIsDirty(false);
                Storage.setCookie(Enums.Cookie.servCompanyName, response.Name);
            }
        } else {
            toast.setToast!!!({
                message: 'There are errors on the page',
                show: true,
                type: Enums.ToastType.error
            });
        }
        setSaving(false);
        return isValid;
    };

    if (!company || !currencies) return null;

    return (
        <div className="settings-container">
            <div className="row">
                <SettingsMenu urlPath={currPath} />
                <div className="settings-card">
                    <div className="row space-between">
                        <div>
                            <h2>Details</h2>
                        
                            <span className='subheading'>Setting up your company details allows you to add contact information, tax rate and your banking details to quotes and invoices.</span>
                        </div>
                        <div>
                            <Button disabled={saving || accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                                text={saving ? "Saving" : "Save"} extraClasses="auto" onClick={saveCompany} />
                        </div>
                    </div>
                    <hr />
                    <div className="row">
                        <div className="column">
                            <h3>Company details</h3>
                            <div className="row">
                                <div className="column">
                                    <SCInput
                                        name="Name"
                                        label="Company Name"
                                        onChange={handleInputChange}
                                        required={true}
                                        value={company.Name}
                                        error={inputErrors.Name}
                                        cypress="data-cy-name"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    <SCInput
                                        name="CompanyRegistrationNo"
                                        label="Company Registration Number"
                                        onChange={handleInputChange}
                                        value={company.CompanyRegistrationNo}
                                        cypress="data-cy-regnumber"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    <SCInput
                                        name="TelephoneNumber"
                                        label="Telephone Number"
                                        type="tel"
                                        onChange={handleInputChange}
                                        value={company.TelephoneNumber}
                                        error={inputErrors.TelephoneNumber}
                                        cypress="data-cy-tel"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    <SCInput
                                        name="WhatsAppNumber"
                                        label={
                                            <Flex align="center" gap={5}>
                                                <Text size="sm" fw={500}>WhatsApp Number</Text>
                                                <Tooltip
                                                    multiline
                                                    maw={460}
                                                    color="scBlue"
                                                    withArrow
                                                    label={
                                                        <>
                                                            This number will be displayed in the Customer Zone so customers can reach you on WhatsApp with their questions.
                                                            <br />
                                                            We recommend using a WhatsApp Business number for the best experience.
                                                        </>
                                                    }
                                                >
                                                    <IconInfoCircle size={18} color={colors.bluePrimary} style={{ cursor: 'help' }} />
                                                </Tooltip>
                                                <NewBadge ml={8} plainMode size="xs" />
                                            </Flex>
                                        }
                                        type="tel"
                                        onChange={handleWhatsAppChange}
                                        onBlur={handleWhatsAppBlur}
                                        value={company.WhatsAppNumber}
                                        error={inputErrors.WhatsAppNumber}
                                        cypress="data-cy-whatsapp"
                                    />
                                    <Text mt={5} c={'gray.7'} size={'sm'}>Defaults to South Africa. For international numbers, include the country code.</Text>
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    <SCInput
                                        name="EmailAddress"
                                        label="Email Address"
                                        onChange={handleInputChange}
                                        value={company.EmailAddress}
                                        error={inputErrors.EmailAddress}
                                        cypress="data-cy-email"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    <SCInput
                                        name="WebsiteAddress"
                                        label="Web Address"
                                        onChange={handleInputChange}
                                        value={company.WebsiteAddress}
                                        cypress="data-cy-website"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    <SCCombobox
                                        name="Currency"
                                        value={selectedCurrency}
                                        textField="Name"
                                        dataItemKey="ID"
                                        options={currencies}
                                        onChange={handleCurrencyChange}
                                        label="Currency"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    <SCInput
                                        name="VATRegistrationNo"
                                        label="VAT Registration No"
                                        onChange={handleInputChange}
                                        value={company.VATRegistrationNo}
                                        cypress="data-cy-vat"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    <SCNumericInput
                                        name="TaxPercentage"
                                        label="Tax percentage"
                                        onChange={handleInputChange}
                                        min={0}
                                        value={company.TaxPercentage}
                                        error={inputErrors.TaxPercentage}
                                        cypress="data-cy-taxpercentage"
                                        format={Enums.NumericFormat.Percentage}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="column">
                                    {/* {company.Timezone === "South Africa Standard Time" ? "South Africa Standard Time" : selectedTimezone?.DisplayName} */}

                                    {company.Timezone === "South Africa Standard Time" ?
                                        <>
                                            <div style={{
                                                marginTop: "1rem",
                                                marginBottom: "0",
                                                display: "inline-block",
                                                fontWeight: 500,
                                                wordBreak: "break-word",
                                                fontSize: "var(--input-label-size, var(--mantine-font-size-sm))"
                                            }}>Time Zone</div>
                                            <SCCheckbox
                                                name="Timezone"
                                                label="South Africa Standard Time"
                                                onChange={() => handleTimezoneChange(null)}
                                                value={selectedTimezone}
                                            />
                                        </> :
                                        <>

                                            <SCCombobox
                                                name="Timezone"
                                                value={selectedTimezone}
                                                textField="DisplayName"
                                                dataItemKey="Id"
                                                options={company.Timezones}
                                                onChange={handleTimezoneChange}
                                                label="Time Zone"
                                                resetValue={resetValues}
                                                required={true}
                                                error={inputErrors.Timezone}
                                            />

                                            <p style={{ fontSize: "0.8rem" }}>Can't find your timezone? Contact support on our chat for assistance.</p>
                                        </>
                                    }
                                    {/* <span style={{fontWeight: "bold", color: colors.bluePrimary, cursor: "pointer"}} onClick={() => hubspotContext.setIsActive(true)}>Contact support on our chat to get help.</span> */}
                                </div>
                            </div>
                        </div>
                        <div className="column column-center">
                            <SCUploadDropzone hint={`Add Logo`} note={`Drag and Drop file to Upload`} multiple={false} uploading={uploading}
                                onChange={onUploadLogoChange} dropzoneOverlaySrc={company.LogoUrl} fileType={Enums.FileType.Image} />
                        </div>
                    </div>
                    <hr />
                    <div className="row">
                        <div className="column">
                            <h3>Company address</h3>
                        </div>
                    </div>
                    <div className="row">
                        <div className="column">
                            <SCTextArea
                                name="Address"
                                label="Physical address"
                                onChange={handleAddressChange}
                                value={address}
                                cypress="data-cy-address"
                                error={inputErrors.Address}
                            />
                        </div>
                        <div className="column">
                            {!sameAddress ?
                                <SCTextArea
                                    name="PostalAddress"
                                    label="Postal address"
                                    onChange={handlePostalAddressChange}
                                    value={postalAddress}
                                    cypress="data-cy-postaladdress"
                                    error={inputErrors.PostalAddress}
                                /> : ''
                            }
                        </div>
                    </div>
                    <div className="row">
                        <div className="column">
                            <SCCheckbox
                                name="SameAddress"
                                onChange={handleSameAddressChange}
                                label="Postal same as Physical"
                                value={sameAddress}
                            />
                        </div>
                    </div>
                    <hr />
                    <div className="row">
                        <div className="column">
                            <h3>Bank details</h3>
                        </div>
                    </div>
                    <div className="row">
                        <div className="column">
                            <Tooltip label={PS.isMasterSystemAdmin() ? "ServCraft Admin cannot change bank details" : ""} disabled={!PS.isMasterSystemAdmin()} position="top-start">
                                <SCInput
                                    name="BankName"
                                    label="Bank Name"
                                    onChange={handleInputChange}
                                    value={company.BankName}
                                    cypress="data-cy-bankname"
                                    disabled={PS.isMasterSystemAdmin()}
                                />
                            </Tooltip>
                        </div>
                        <div className="column">
                            <Tooltip label={PS.isMasterSystemAdmin() ? "ServCraft Admin cannot change bank details" : ""} disabled={!PS.isMasterSystemAdmin()} position="top-start">
                                <SCInput
                                    name="BankBranchCode"
                                    label="Branch Code"
                                    onChange={handleInputChange}
                                    value={company.BankBranchCode}
                                    cypress="data-cy-bankbranchcode"
                                    disabled={PS.isMasterSystemAdmin()}
                                />
                            </Tooltip>
                        </div>
                    </div>
                    <div className="row">
                        <div className="column">
                            <Tooltip label={PS.isMasterSystemAdmin() ? "ServCraft Admin cannot change bank details" : ""} disabled={!PS.isMasterSystemAdmin()} position="top-start">
                                <SCInput
                                    name="BankAccountName"
                                    label="Account Name"
                                    onChange={handleInputChange}
                                    value={company.BankAccountName}
                                    cypress="data-cy-bankaccountname"
                                    disabled={PS.isMasterSystemAdmin()}
                                />
                            </Tooltip>
                        </div>
                        <div className="column">
                            <Tooltip label={PS.isMasterSystemAdmin() ? "ServCraft Admin cannot change bank details" : ""} disabled={!PS.isMasterSystemAdmin()} position="top-start">
                                <SCInput
                                    name="BankAccountNumber"
                                    label="Account Number"
                                    onChange={handleInputChange}
                                    value={company.BankAccountNumber}
                                    cypress="data-cy-bankaccountnumber"
                                    disabled={PS.isMasterSystemAdmin()}
                                />
                            </Tooltip>
                        </div>
                    </div>
                    <hr />
                    <div className="row">
                        <div className="column">
                            <h3>Communication settings</h3>
                        </div>
                    </div>
                    <div className="row">
                        <div className="column">
                            <SCInput
                                name="EmailFromName"
                                label="Reply to Name"
                                onChange={handleInputChange}
                                value={company.EmailFromName}
                                cypress="data-cy-emailfromname"
                            />
                        </div>
                        <div className="column">
                            <SCInput
                                name="EmailFromAddress"
                                label="Reply to Address"
                                onChange={handleInputChange}
                                value={company.EmailFromAddress}
                                error={inputErrors.EmailFromAddress}
                                cypress="data-cy-emailfromaddress"
                            />

                            <SCCheckbox
                                name="EmailFromEmployee"
                                label="Reply to Employee"
                                onChange={(value: any) => {
                                    handleInputChange({ name: 'EmailFromEmployee', value })
                                }}
                                value={company.EmailFromEmployee}
                                hint='Only applicable Quotes, Invoices, Purchase Orders & Queries'
                            />
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

            <style jsx>{`
                hr {
                    margin-top: 1.5rem;
                }
                .name {
                    max-width: 50%;
                }
                .logo-container {
                    margin-top: 1.5rem;
                    width: 15.625rem;
                }
                .logo {
                    align-items: center;
                    border: 1px solid ${colors.borderGrey};
                    border-radius: ${layout.cardRadius};
                    display: flex;
                    height: 15.625rem;
                    justify-content: center;
                    object-fit: contain;
                    overflow: hidden;
                    position: relative;
                    width: 100%;
                }
                .logo img {
                    height: auto;
                    max-width: 100%;
                }
                .view {
                    align-items: center;
                    background-color: rgba(28, 37, 44, 0.55);
                    border-radius: ${layout.cardRadius};
                    display: flex;
                    height: 15.625rem;
                    justify-content: center;
                    left: 0;
                    opacity: 0;
                    position: absolute;
                    top: 0;
                    transition: opacity 0.2s ease-out;
                    width: 100%;
                }
                .view:hover {
                    opacity: 1;
                }
                .view-button {
                    align-items: center;
                    background-color: ${colors.white};
                    border: 1px solid ${colors.bluePrimary};
                    border-radius: ${layout.buttonRadius};
                    color: ${colors.bluePrimary};
                    cursor: pointer;
                    display: flex;
                    font-weight: bold;
                    height: 2.5rem;
                    justify-content: center;
                    width: 9rem;
                }
                .view-shown {
                    align-items: center;
                    background-color: ${colors.white};
                    border-radius: ${layout.cardRadius};
                    display: flex;
                    height: 15.625rem;
                    justify-content: center;
                    left: 0;
                    opacity: 0;
                    position: absolute;
                    top: 0;
                    width: 100%;
                    opacity: 1;
                }
                .view-button-shown {
                    align-items: center;
                    background-color: ${colors.white};
                    border: 1px solid ${colors.bluePrimary};
                    border-radius: ${layout.buttonRadius};
                    color: ${colors.bluePrimary};
                    cursor: pointer;
                    display: flex;
                    font-weight: bold;
                    height: 2.5rem;
                    justify-content: center;
                    width: 9rem;
                }
                .hidden-file-input {
                    display: none;
                }
                .row {
                    display: flex;
                }
                .space-between {
                    justify-content: space-between;
                }
                .column {
                    display: flex;
                    flex-direction: column;
                    width: ${layout.inputWidth};
                }
                .column-end {
                    align-items: flex-end;
                }
                .full-width {
                    width: 100% !important;
                }
                .column-center {
                    align-items: center;
                }
                .column-margin {
                    margin-left: 24px;
                }
                .column + .column {
                    margin-left: 1.25rem;
                }
                p {
                    color: ${colors.darkPrimary};
                }
            `}</style>
        </div>
    )
}

export default ManageCompany;

export const Route = createFileRoute('/_authenticated/settings/company/manage')({ component: ManageCompany })
